import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Award, Eye, Calendar, Loader2, Copy } from "lucide-react";
import AppLayout from "../components/AppLayout";
import { api } from "../api/client";
import { toast } from "sonner";

const fmtDate = (d) => {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("en-GB", { month: "short", day: "numeric", year: "numeric" }); }
  catch { return d; }
};

export default function Certificates() {
  const [certs,   setCerts]   = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/certificates")
      .then(setCerts)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const copyVerify = (certId) => {
    navigator.clipboard.writeText(`${window.location.origin}/verify/${certId}`);
    toast.success("Verification link copied");
  };

  return (
    <AppLayout searchPlaceholder="Search certificates...">
      <div className="mb-10">
        <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight">Certificates</h1>
        <p className="text-sm text-gray-400 mt-2">
          {loading ? "Loading…" : `${certs.length} certificate${certs.length !== 1 ? "s" : ""} earned`}
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-gray-500">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading certificates…
        </div>
      ) : certs.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center border border-white/[0.06] rounded-2xl">
          <Award className="w-10 h-10 text-gray-700 mb-3" />
          <p className="text-gray-400 font-semibold">No certificates yet</p>
          <p className="text-sm text-gray-600 mt-1">Complete a course to earn your first certificate.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {certs.map((c) => (
            <div key={c.id} data-testid={`cert-${c.id}`}
              className="group relative bg-white/[0.03] rounded-3xl border border-white/[0.08] overflow-hidden p-6 hover:border-lime/30 transition-all hover:-translate-y-1">
              <div className="absolute -top-6 -right-6 w-32 h-32 rounded-full bg-lime/10 blur-2xl group-hover:bg-lime/20 transition-colors pointer-events-none" />
              <div className="relative">
                <div className="w-12 h-12 rounded-2xl bg-lime/15 border border-lime/30 flex items-center justify-center mb-5">
                  <Award className="w-6 h-6 text-lime" />
                </div>
                <div className="text-xs uppercase tracking-widest text-gray-500 font-semibold">Certificate of Completion</div>
                <h3 className="font-display font-bold text-xl mt-2 leading-tight line-clamp-2">
                  {c.course_title || c.course_name || "—"}
                </h3>
                <div className="mt-4 flex items-center gap-2 text-xs text-gray-400">
                  <Calendar className="w-3.5 h-3.5" /> Issued {fmtDate(c.issued_at)}
                </div>
                {(c.instructor || c.grade) && (
                  <div className="mt-1 text-xs text-gray-500">
                    {c.instructor && `By ${c.instructor}`}
                    {c.grade && <> · Grade <span className="text-lime font-bold">{c.grade}</span></>}
                  </div>
                )}
                <div className="mt-6 pt-5 border-t border-white/[0.06] flex items-center justify-between gap-2">
                  <span className="text-[11px] text-gray-500 font-mono truncate">{c.code || c.id?.slice(0, 12)}</span>
                  <div className="flex items-center gap-2 shrink-0">
                    <button onClick={() => copyVerify(c.id)}
                      title="Copy verification link"
                      className="p-1.5 rounded-lg text-gray-600 hover:text-white hover:bg-white/[0.06] transition-colors">
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                    <Link to={`/certificates/${c.id}`}
                      className="inline-flex items-center gap-1.5 text-sm font-bold text-lime hover:gap-2.5 transition-all">
                      <Eye className="w-3.5 h-3.5" /> View
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </AppLayout>
  );
}
